import { getOperationName } from '@apollo/client/utilities'
import { Button, DatePicker, Form, Input, Modal, Popconfirm, message } from 'antd'
import { FormInstance } from 'antd/lib/form'
import cls from 'classnames'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  CareerProfileFragment,
  ExperienceType,
  GetCareerProfileDocument,
  useCareerProfileDeleteMutation,
  useCareerProfileUpdateMutation,
} from '@/generated/graphql'
import { ga, traceEvent } from '@/utils/ga'

import s from './user-info-background-modal.module.less'

export const UserInfoBackgroundForm: React.FC<{
  form: FormInstance<{
    organization: string
    position?: string | undefined
    startTime: moment.Moment
    endTime: moment.Moment
    description?: string | undefined
  }>
  experience?: CareerProfileFragment
  isCareer: boolean
}> = ({ form, experience, isCareer }) => {
  const { t } = useTranslation()

  React.useEffect(() => {
    const values = experience
      ? [
          { name: 'organization', value: experience.organization },
          { name: 'position', value: experience.position },
          {
            name: 'startTime',
            value: experience.duration.split('_').map((value) => moment(value))[0],
          },
          {
            name: 'endTime',
            value: experience.duration.split('_').map((value) => moment(value))[1],
          },
          { name: 'description', value: experience.jobDescription },
        ]
      : [
          { name: 'organization', value: '' },
          { name: 'position', value: '' },
          { name: 'startTime', value: moment() },
          { name: 'endTime', value: moment() },
          { name: 'description', value: '' },
        ]
    form.setFields(values)
  }, [experience])

  return (
    <Form labelCol={{ span: 4 }} form={form} preserve={false}>
      <Form.Item label={t(`user.info.background.${isCareer ? 'company' : 'school'}`)} name='organization' required>
        <Input />
      </Form.Item>
      <Form.Item label={t(`user.info.background.${isCareer ? 'position' : 'major'}`)} name='position'>
        <Input />
      </Form.Item>
      <Form.Item label={t('profile.workExperience.duration')} className={s.time}>
        <Form.Item name='startTime' required>
          <DatePicker dropdownClassName={s.yearPicker} picker='year' allowClear={false} />
        </Form.Item>
        <span style={{ margin: '0 8px' }}>{t('user.info.background.to')}</span>
        <Form.Item name='endTime' required>
          <DatePicker dropdownClassName={s.yearPicker} picker='year' allowClear={false} />
        </Form.Item>
      </Form.Item>
      <Form.Item label={t('user.info.background.description')} name='description'>
        <Input.TextArea />
      </Form.Item>
    </Form>
  )
}

export const UserInfoBackgroundModal: React.FC<{
  input: {
    experience?: CareerProfileFragment | undefined
    type: ExperienceType
  }
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ input, visible, setVisible }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<{
    organization: string
    position?: string
    startTime: moment.Moment
    endTime: moment.Moment
    description?: string
  }>()

  const { type } = input
  const experience = input?.experience

  const [upsertCareerProfile] = useCareerProfileUpdateMutation({
    refetchQueries: [getOperationName(GetCareerProfileDocument)!],
    onCompleted(data) {
      const res = data.careerProfileUpdate?.[0]
      let event
      const category = traceEvent.profile
      if (experience) {
        event =
          type === ExperienceType.Education
            ? category.events.click_upsert_education
            : category.events.click_upsert_career
      } else {
        event =
          type === ExperienceType.Education
            ? category.events.click_upsert_education
            : category.events.click_upsert_career
      }
      if (res) {
        ga('event', event, {
          event_category: category.event_category,
        })
      }
      message.success(t(`user.info.background.${experience ? 'update' : 'add'}Message`))
    },
    onError() {
      message.error(t(`user.info.background.${experience ? 'update' : 'add'}ErrorMessage`))
    },
  })

  const [deleteCareerProfile] = useCareerProfileDeleteMutation({
    refetchQueries: [getOperationName(GetCareerProfileDocument)!],
  })

  const isCareer = type === ExperienceType.Career

  const onOK = async () => {
    const values = await form.validateFields()
    upsertCareerProfile({
      variables: {
        careerProfile: {
          pastExperienceId: experience?.pastExperienceId,
          organization: values.organization || '',
          jobDescription: values.description || '',
          duration: [values.startTime, values.endTime].map((value) => value.format('YYYY-MM-DD')).join('_'),
          position: values.position || '',
          experienceType: type,
        },
      },
    })

    setVisible(false)
  }

  const footer = (
    <div className={cls(s.footer, { [s.noDelete]: !experience })}>
      {experience && (
        <div className={s.delete}>
          <Popconfirm
            title={t(
              `user.info.${experience.experienceType === ExperienceType.Career ? 'career' : 'education'}.deleteConfirm`
            )}
            onConfirm={async () => {
              await deleteCareerProfile({
                variables: {
                  careerProfileId: experience!.pastExperienceId!,
                },
              })
              setVisible(false)
            }}
          >
            <Button danger>{t('user.info.background.delete')}</Button>
          </Popconfirm>
        </div>
      )}
      <div className={s.actions}>
        <Button type='text' onClick={() => setVisible(false)}>
          {t('user.info.background.cancel')}
        </Button>
        <Button type='primary' onClick={() => onOK()}>
          {t(`user.info.background.${experience ? 'save' : 'add'}`)}
        </Button>
      </div>
    </div>
  )

  return (
    <Modal
      title={t(`user.info.${isCareer ? 'career' : 'education'}.${experience ? 'update' : 'placeholder'}`)}
      visible={visible}
      footer={footer}
      maskClosable={false}
      closable={false}
      destroyOnClose
    >
      <UserInfoBackgroundForm form={form} experience={experience} isCareer={isCareer} />
    </Modal>
  )
}
